import { useNProgress } from '@tanem/react-nprogress';
import { Col, ConfigProvider, Grid, Layout, Row, Typography } from 'antd';
import React from 'react';
import { Link, Outlet, useLocation, useNavigation } from 'react-router-dom';
import { decafThemeDark, useDecafTheme } from '../theme';
import { DecafMenuItem, UserProfileDropdownItem } from '../types';
import { CommandPaletteHost, CommandPaletteIndicator, CommandPaletteItem } from './CommandPalette';
import DocumentationButton from './DocumentationButton';
import Logo from './Logo';
import DecafMenu, { DecafPoweredBy } from './Menu';
import PageScroller from './PageScroller';
import ScreenShotter from './Screenshotter';
import ThemeSwitcher from './ThemeSwitcher';
import { UserProfileDropdown } from './UserProfileDropdown';
import VersionSelector from './VersionSelector';
import ZendeskWidget from './ZendeskWidget';

export interface DecafLayoutProps {
  menu: DecafMenuItem[];
  appName: string;
  children?: React.ReactNode;
  /** Resolved palette items, or `null` to disable the feature. */
  commandPaletteItems?: CommandPaletteItem[] | null;
  /** Consumer-contributed items injected into the user profile dropdown. */
  userProfileDropdownItems?: UserProfileDropdownItem[];
}

export default function DecafLayout(props: DecafLayoutProps) {
  const location = useLocation();
  const navigation = useNavigation();
  const breakpoints = Grid.useBreakpoint();
  const { theme: currentTheme, toggleTheme: setCurrentTheme } = useDecafTheme();

  return (
    <Layout style={{ height: '100%' }}>
      <DecafLayoutProgress isAnimating={navigation.state === 'loading'} key={location.key} />

      <ConfigProvider theme={decafThemeDark}>
        <Layout.Header id="decaf-header">
          <DecafLayoutBrand title={props.appName} />
          <DecafMenu title={props.appName} menu={props.menu} />
          {props.commandPaletteItems ? <CommandPaletteIndicator /> : null}
          <UserProfileDropdown items={props.userProfileDropdownItems} />
        </Layout.Header>
      </ConfigProvider>

      <Outlet />
      {props.children ?? null}
      {props.commandPaletteItems ? <CommandPaletteHost items={props.commandPaletteItems} /> : null}

      <ConfigProvider theme={decafThemeDark}>
        <Layout.Footer id="decaf-footer">
          <Row justify="space-between" align={'middle'}>
            <Col span={breakpoints.lg ? 10 : 12}>
              <ZendeskWidget />
              <span style={{ display: 'inline-block', width: 1 }} />
              <DocumentationButton />
            </Col>

            <Col span={breakpoints.lg ? 4 : 0} style={{ textAlign: 'center' }}>
              <DecafPoweredBy />
            </Col>

            <Col span={breakpoints.lg ? 10 : 12} style={{ justifyContent: 'flex-end', display: 'flex', gap: 10 }}>
              <VersionSelector />
              <ThemeSwitcher theme={currentTheme} onChange={setCurrentTheme} />
              <ScreenShotter />
              <PageScroller />
            </Col>
          </Row>
        </Layout.Footer>
      </ConfigProvider>
    </Layout>
  );
}

export function DecafLayoutBrand({ title }: { title: string }) {
  const breakpoints = Grid.useBreakpoint();

  return (
    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Logo />

      {breakpoints.lg && (
        <Typography.Title
          level={1}
          style={{
            margin: 0,
            fontWeight: 'normal',
            fontSize: 20,
            color: 'rgba(255, 255, 255, 0.75)',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography.Title>
      )}
    </Link>
  );
}

export function DecafLayoutProgress({ isAnimating }: { isAnimating: boolean }) {
  const { animationDuration, isFinished, progress } = useNProgress({ isAnimating });

  return (
    <div
      style={{
        opacity: isFinished ? 0 : 1,
        pointerEvents: 'none',
        transition: `opacity ${animationDuration}ms linear`,
      }}
    >
      <div
        style={{
          background: '#29d',
          height: 2,
          left: 0,
          marginLeft: `${(-1 + progress) * 100}%`,
          position: 'fixed',
          top: 0,
          transition: `margin-left ${animationDuration}ms linear`,
          width: '100%',
          zIndex: 1031,
        }}
      >
        <div
          style={{
            boxShadow: '0 0 10px #29d, 0 0 5px #29d',
            display: 'block',
            height: '100%',
            opacity: 1,
            position: 'absolute',
            right: 0,
            transform: 'rotate(3deg) translate(0px, -4px)',
            width: 100,
          }}
        />
      </div>
    </div>
  );
}
